/**
 * Deploy-time configuration supplied to the SPA as `window.__ENV__` by `/config.js`, which the
 * deployment mounts over the image's baked-in fallback (see docs/RUNTIME-CONFIG.md for the contract
 * the Helm chart implements). This reader coerces the raw string values to typed values with safe
 * defaults so callers never touch the global. In local dev the fallback `public/config.js` supplies
 * an empty object, so everything defaults to "off".
 */
export type RuntimeEnv = {
  areDashboardsEnabled: boolean
  dashboardsUrl?: string
  sentryDsn?: string
  /**
   * True when nothing vouched for the config the browser received, so every other field is at its
   * default regardless of how the deploy is configured. This is the difference between "the operator
   * turned everything off" and "config never arrived" — the silent failure behind ISS-8244.
   */
  isRuntimeConfigMissing: boolean
}

export function getRuntimeEnv(): RuntimeEnv {
  const raw = window.__ENV__ ?? {}
  return {
    areDashboardsEnabled: raw.DASHBOARDS_ENABLED === 'true',
    dashboardsUrl: raw.DASHBOARDS_URL !== undefined && raw.DASHBOARDS_URL !== '' ? raw.DASHBOARDS_URL : undefined,
    sentryDsn: raw.SENTRY_DSN !== undefined && raw.SENTRY_DSN !== '' ? raw.SENTRY_DSN : undefined,
    // Deliberately fails closed: only an explicit `true` counts as configured. An absent sentinel
    // means /config.js 404'd, was truncated, or came from an image whose deployment supplied no
    // config — all cases where the flags below are meaningless rather than merely off.
    isRuntimeConfigMissing: window.__ENV_CONFIGURED__ !== true,
  }
}

/**
 * Surfaces missing runtime config in the browser console at boot. Without this the failure is
 * invisible from the outside: every flag simply reads as "off" and Sentry stays uninitialized, which
 * is indistinguishable from a deliberately minimal deploy. That is how ISS-8244 shipped unnoticed.
 * Sentry cannot carry this signal, since missing config is exactly the case where it has no DSN.
 */
export function warnIfRuntimeConfigMissing(): void {
  if (!getRuntimeEnv().isRuntimeConfigMissing) {
    return
  }

  console.error(
    'No deploy-time runtime config reached the browser, so error reporting and the Dashboards link ' +
      'are off no matter how they are configured. The deployment should mount /config.js at ' +
      '/var/lib/pixee/runtime-config/config.js — in Pixee Enterprise Server that is the ' +
      'user-platform ConfigMap. See packages/user-platform/docs/RUNTIME-CONFIG.md.'
  )
}
